import { Alert, Stack, Typography } from "@mui/material";
import { memo, useCallback, useEffect, useState } from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import useFetch from "../../../hooks/useFetch";
import { IInputFile } from "../../../models/input-file";
import { ITicketAutomationReceivedRule } from "../../../services/tasySchedMenu.service";
import { ScheduleAppointmentService } from "../../Schedule/appointment.service";
import { TAppointment } from "../../Schedule/NewSchedule/schema";
import { InputFile } from "@4uhub/lib4uhub";
import GlobalAxios from "../../../axios/axios-config";

interface IExamAttachmentsProps {
  rule?: ITicketAutomationReceivedRule | null;
  onFileSelect: (value: boolean) => void;
}

export interface IExamAttachmentRule {
  id: string;
  displayOrder: number;
  message: string;
  messageError?: string;
}

const stripHtml = (value?: string) =>
  (value || "")
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const FILE_ROUTE = process.env.REACT_APP_FILEMANAGER + "/api/v1/Upload";

const ExamAttachments: React.FC<IExamAttachmentsProps> = ({
  rule,
  onFileSelect,
}) => {
  const [rules, setRules] = useState<IExamAttachmentRule[]>([]);

  const { control, getValues, setValue } = useFormContext<TAppointment>();

  const { t } = useTranslation();

  const { sendRequest: getRules, loading } = useFetch(
    ScheduleAppointmentService.getScheduleAttachmentRules,
  );

  const deletedFilesIds = useWatch({control, name: "scheduleAttachments.deletedFilesIds"})

  console.log(deletedFilesIds)

  const fetchRules = useCallback(async () => {
    if (!rule?.id) return;

    const values = getValues() as any;
    const schedule = values.schedule;
    const insuranceCode =
      schedule?.insuranceCode ||
      values.insurance?.insuranceIntegrationId ||
      values.insurance?.code;

    const { data, success } = await getRules({
      ticketAutomationReceivedRuleId: rule.id,
      calendarIdentifier: schedule?.calendarIdentifier,
      insuranceCode: insuranceCode ? Number(insuranceCode) : undefined,
      procedureInternalCode: schedule?.procedureInternalCode,
      calendarPreferencesGrouped: schedule?.calendarPreferencesGrouped,
    });

    if (success && data) {
      setRules(data);
    }
  }, [getRules, getValues, rule?.id]);

  useEffect(() => {
    fetchRules();
  }, [fetchRules]);

  if (loading) {
    return (
      <Typography>
        {t("page.schedules.newSchedule.attachments.loading")}
      </Typography>
    );
  }

  if (!rules.length) {
    return (
      <Alert severity="info">
        {t("page.schedules.newSchedule.attachments.empty")}
      </Alert>
    );
  }

  return (
    <Stack gap={3}>
      <Stack gap={0.75}>
        <Typography variant="h5" fontWeight={700}>
          {t("page.schedules.newSchedule.attachments.title")}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t("page.schedules.newSchedule.attachments.description")}
        </Typography>
      </Stack>

      {rules.map((rule) => (
        <Controller
          key={rule.id}
          control={control}
          name={`scheduleAttachments.files.${rule.id}` as any}
          render={({ field }) => (
            <InputFile
              axios={GlobalAxios}
              fileRoute={FILE_ROUTE}
              label={stripHtml(rule.message)}
              accept=".jpg, .jpeg, .png, .webp, .pdf"
              maxSize={10}
              value={field.value as IInputFile | undefined}
              onFileSelected={(value) => {
                const deletedFilesIds =
                  getValues("scheduleAttachments.deletedFilesIds") ?? [];
                deletedFilesIds.push(value.id);
                setValue(
                  "scheduleAttachments.deletedFilesIds",
                  deletedFilesIds,
                );
              }}
              onChange={(val) => {
                onFileSelect(true);
                field.onChange(val);
              }}
              uploadFileProps={{
                mediaTypeCode: "1",
                mediaTypeIdentifier: "TicketDefaultDocMediaType",
                parentName: "ScheduleAttachment",
                type: "Private",
              }}
            />
          )}
        />
      ))}
    </Stack>
  );
};

export default memo(ExamAttachments);
